import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { useMediaQuery } from 'react-responsive';
import { styles as mobileStyles } from '../styles/pages/cronJobDashboardStyles';
import { styles as desktopStyles } from '../styles/pages/cronJobDashboardDesktopStyles';
import { cronService } from '../services/cronService';

interface CronStatus {
  jobName: string;
  cronExpression: string;
  isEnabled: boolean;
  isRunning: boolean;
  lastModifiedAt: string;
  lastModifiedBy: string | null;
  timezone?: string;
  nextExecution: string;
  currentExecution?: {
    startedAt: string;
    duration: number;
  } | null;
  lastExecution?: {
    startedAt: string;
    completedAt: string;
    status: string;
    recordsProcessed: number;
    errorMessage: string | null;
  } | null;
}

interface ExecutionHistory {
  id: number;
  jobName: string;
  status: string;
  startedAt: string;
  completedAt: string;
  recordsProcessed: number;
  errorMessage: string | null;
}

const CronJobDashboard: React.FC = () => {
  const isDesktop = useMediaQuery({ minWidth: 768 });
  const styles = isDesktop ? { ...mobileStyles, ...desktopStyles } : mobileStyles;
  
  const [cronStatus, setCronStatus] = useState<CronStatus | null>(null);
  const [executionHistory, setExecutionHistory] = useState<ExecutionHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  const statusIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  const itemsPerPage = 20;
  
  useEffect(() => {
    loadCronStatus();
    loadExecutionHistory();
    
    return () => {
      if (statusIntervalRef.current) {
        clearInterval(statusIntervalRef.current);
      }
    };
  }, []);
  
  useEffect(() => {
    loadExecutionHistory();
  }, [currentPage]);
  
  const loadCronStatus = async () => {
    try {
      const response = await cronService.getCronStatus();
      if (response.success && response.data?.data) {
        setCronStatus(response.data.data);
        
        if (response.data.data.isRunning && !statusIntervalRef.current) {
          statusIntervalRef.current = setInterval(checkRunningStatus, 5000);
        } else if (!response.data.data.isRunning && statusIntervalRef.current) {
          clearInterval(statusIntervalRef.current);
          statusIntervalRef.current = null;
          loadExecutionHistory();
        }
      }
      setLoading(false);
    } catch (err) {
      setError('크론잡 상태를 불러오는데 실패했습니다.');
      setLoading(false);
    }
  };
  
  const checkRunningStatus = async () => {
    try {
      const response = await cronService.getCronStatus();
      if (response.success && response.data?.data) {
        setCronStatus(response.data.data);
        
        if (!response.data.data.isRunning && statusIntervalRef.current) {
          clearInterval(statusIntervalRef.current);
          statusIntervalRef.current = null;
          loadExecutionHistory();
        }
      }
    } catch (err) {
      console.error('Status check failed:', err);
    }
  };
  
  const loadExecutionHistory = async () => {
    try {
      const offset = (currentPage - 1) * itemsPerPage;
      const response = await cronService.getExecutionHistory(itemsPerPage, offset);
      if (response.success && response.data?.data) {
        // API 응답이 중첩된 구조인 경우
        if (response.data.data.history) {
          setExecutionHistory(response.data.data.history);
          setTotalPages(Math.ceil(response.data.data.total / itemsPerPage));
        }
      }
    } catch (err) {
      setError('실행 이력을 불러오는데 실패했습니다.');
    }
  };
  
  const handleManualSync = async () => {
    setSyncing(true);
    setError(null);
    
    // 즉시 실행 중 상태로 업데이트 (중복 클릭 방지)
    if (cronStatus) {
      setCronStatus({
        ...cronStatus,
        isRunning: true,
        currentExecution: {
          startedAt: new Date().toISOString(),
          duration: 0
        }
      });
    }
    
    try {
      const response = await cronService.triggerManualSync();
      if (response.success) {
        // 즉시 상태를 업데이트하고 주기적으로 확인 시작
        await loadCronStatus();
        
        // 이미 interval이 없을 때만 시작
        if (!statusIntervalRef.current) {
          statusIntervalRef.current = setInterval(checkRunningStatus, 3000);
        }
      } else {
        setError(response.message || '동기화 시작에 실패했습니다.');
        // 실패 시 상태 원복
        await loadCronStatus();
      }
    } catch (err: any) {
      if (err.status === 409) {
        setError('이미 동기화가 진행 중입니다.');
      } else {
        setError('동기화 시작에 실패했습니다.');
      }
      // 에러 시 상태 원복
      await loadCronStatus();
    } finally {
      setSyncing(false);
    }
  };
  
  const handleRefresh = () => {
    setLoading(true);
    setError(null);
    loadCronStatus();
    loadExecutionHistory();
  };
  
  const formatDate = (dateString: string, isCompletedAt?: boolean, status?: string) => {
    // 완료 시간이고 실행 중인 경우
    if (isCompletedAt && status === 'started') {
      return '-';
    }
    
    // 날짜가 없거나 잘못된 경우
    if (!dateString) {
      return '-';
    }
    
    // DB의 날짜를 그대로 표시
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };
  
  const formatDuration = (start: string, end: string, status?: string) => {
    // 실행 중이거나 종료 시간이 없는 경우
    if (status === 'started' || !end || end === start) {
      return '-';
    }
    
    const duration = new Date(end).getTime() - new Date(start).getTime();
    
    // 음수 값인 경우 (잘못된 데이터)
    if (duration < 0) {
      return '-';
    }
    
    const minutes = Math.floor(duration / 60000);
    const seconds = Math.floor((duration % 60000) / 1000);
    return `${minutes}분 ${seconds}초`;
  };
  
  const formatRunningDuration = (durationMs: number) => {
    const minutes = Math.floor(durationMs / 60000);
    const seconds = Math.floor((durationMs % 60000) / 1000);
    return `${minutes}분 ${seconds}초 경과`;
  };
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return '#52c41a';
      case 'failed':
        return '#ff4d4f';
      case 'started':
        return '#faad14';
      default:
        return '#666';
    }
  };
  
  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return '성공';
      case 'failed':
        return '실패';
      case 'started':
        return '진행중';
      default:
        return status;
    }
  };
  
  //원래 UTC로 날짜를 받으려 했는데 UTC+9 기준으로 날짜를 받기로 함
  const getCronDescription = (expression: string) => {
    if (!expression) {
      return '스케줄 정보 없음';
    }
    const parts = expression.split(' ');
    if (parts.length >= 2) {
      const utcHour = parseInt(parts[1]);
      const minute = parseInt(parts[0]);
      
      let kstHour = utcHour;
      let nextDay = false;
      
      if (kstHour >= 24) {
        kstHour = kstHour - 24;
        nextDay = true;
      }
      
      if (parts[2] === '*' && parts[3] === '*' && parts[4] === '*') {
        // 매일 실행
        if (minute === 0) {
          let timeStr = '';
          if (kstHour === 0) timeStr = '자정';
          else if (kstHour < 12) timeStr = `오전 ${kstHour}시`;
          else if (kstHour === 12) timeStr = '정오';
          else timeStr = `오후 ${kstHour - 12}시`;
          
          return `매일 ${timeStr}${nextDay ? ' (다음날)' : ''} (KST)`;
        } else {
          const hourStr = kstHour < 12 ? `오전 ${kstHour}시` : `오후 ${kstHour - 12}시`;
          return `매일 ${hourStr} ${minute}분${nextDay ? ' (다음날)' : ''} (KST)`;
        }
      } else if (parts[1].includes('*/')) {
        // 시간 간격
        const interval = parts[1].replace('*/', '');
        return `${interval}시간마다`;
      }
    }
    return expression;
  };
  
  const renderContent = () => (
    <View style={styles.container}>
      {error && (
        <View style={styles.errorBanner}>
          <Text style={styles.errorText}>{error}</Text>
          <TouchableOpacity onPress={() => setError(null)}>
            <Text style={styles.errorClose}>✕</Text>
          </TouchableOpacity>
        </View>
      )}
      
      <ScrollView 
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}
      >
        {loading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#1890ff" />
            <Text style={styles.loadingText}>데이터를 불러오는 중...</Text>
          </View>
        ) : cronStatus && (
          <View style={styles.content}>
            {/* 헤더 섹션 */}
            <View style={styles.headerSection}>
              <View style={styles.headerContent}>
                <View>
                  <Text style={styles.pageTitle}>크론잡 관리</Text>
                  <Text style={styles.pageSubtitle}>Redmine 동기화 작업 모니터링</Text>
                </View>
                <TouchableOpacity 
                  style={styles.refreshButton}
                  onPress={handleRefresh}
                >
                  <Text style={styles.refreshText}>새로고침</Text>
                </TouchableOpacity>
              </View>
            </View>
            
            {/* 상태 개요 섹션 */}
            <View style={styles.overviewSection}>
              <View style={styles.overviewGrid}>
                {/* 현재 상태 카드 */}
                <View style={styles.overviewCard}>
                  <View style={styles.overviewCardHeader}>
                    <Text style={styles.overviewCardTitle}>현재 상태</Text>
                    <View style={[
                      styles.statusIndicator,
                      { backgroundColor: cronStatus.isRunning ? '#faad14' : '#52c41a' }
                    ]}>
                      <Text style={styles.statusIndicatorText}>
                        {cronStatus.isRunning ? '실행 중' : '대기 중'}
                      </Text>
                    </View>
                  </View>
                  <View style={styles.overviewCardBody}>
                    <View style={styles.overviewInfo}>
                      <Text style={styles.overviewLabel}>활성화 상태</Text>
                      <Text style={[
                        styles.overviewValue,
                        { color: cronStatus.isEnabled ? '#52c41a' : '#ff4d4f' }
                      ]}>
                        {cronStatus.isEnabled ? '활성화' : '비활성화'}
                      </Text>
                    </View>
                    <View style={styles.overviewInfo}>
                      <Text style={styles.overviewLabel}>스케줄</Text>
                      <Text style={styles.overviewValue}>{getCronDescription(cronStatus?.cronExpression)}</Text>
                    </View>
                    {cronStatus.isRunning && cronStatus.currentExecution && (
                      <View style={styles.overviewInfo}>
                        <Text style={styles.overviewLabel}>실행 시간</Text>
                        <Text style={[styles.overviewValue, { color: '#faad14' }]}>
                          {formatRunningDuration(cronStatus.currentExecution.duration)}
                        </Text>
                      </View>
                    )}
                  </View>
                </View>
                
                {/* 다음 실행 카드 */}
                <View style={styles.overviewCard}>
                  <View style={styles.overviewCardHeader}>
                    <Text style={styles.overviewCardTitle}>다음 실행</Text>
                  </View>
                  <View style={styles.overviewCardBody}>
                    <Text style={styles.nextRunTime}>{formatDate(cronStatus.nextExecution)}</Text>
                    <TouchableOpacity
                      style={[
                        styles.syncButton,
                        (!cronStatus.isEnabled || cronStatus.isRunning || syncing) && styles.syncButtonDisabled
                      ]}
                      onPress={handleManualSync}
                      disabled={!cronStatus.isEnabled || cronStatus.isRunning || syncing}
                    >
                      {syncing ? (
                        <ActivityIndicator size="small" color="#fff" />
                      ) : (
                        <Text style={styles.syncButtonText}>
                          {!cronStatus.isEnabled ? '비활성화됨' :
                           cronStatus.isRunning ? '실행 중...' :
                           '수동 실행'}
                        </Text>
                      )}
                    </TouchableOpacity>
                  </View>
                </View>
                
                {/* 마지막 실행 카드 */}
                {cronStatus.lastExecution && (
                  <View style={styles.overviewCard}>
                    <View style={styles.overviewCardHeader}>
                      <Text style={styles.overviewCardTitle}>마지막 실행</Text>
                      <View style={[
                        styles.statusIndicator,
                        { backgroundColor: getStatusColor(cronStatus.lastExecution.status) }
                      ]}>
                        <Text style={styles.statusIndicatorText}>
                          {getStatusText(cronStatus.lastExecution.status)}
                        </Text>
                      </View>
                    </View>
                    <View style={styles.overviewCardBody}>
                      <View style={styles.overviewInfo}>
                        <Text style={styles.overviewLabel}>실행 시간</Text>
                        <Text style={styles.overviewValue}>{formatDate(cronStatus.lastExecution.startedAt)}</Text>
                      </View>
                      <View style={styles.overviewInfo}>
                        <Text style={styles.overviewLabel}>처리 건수</Text>
                        <Text style={styles.overviewValue}>{cronStatus.lastExecution.recordsProcessed}개</Text>
                      </View>
                      <View style={styles.overviewInfo}>
                        <Text style={styles.overviewLabel}>소요 시간</Text>
                        <Text style={styles.overviewValue}>
                          {formatDuration(cronStatus.lastExecution.startedAt, cronStatus.lastExecution.completedAt, cronStatus.lastExecution.status)}
                        </Text>
                      </View>
                    </View>
                  </View>
                )}
              </View>
            </View>
            
            {/* 실행 이력 섹션 */}
            <View style={styles.historySection}>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionTitle}>실행 이력</Text>
              </View>
              
              {executionHistory && executionHistory.length > 0 ? (
                <>
                  <View style={styles.historyTable}>
                    <View style={styles.tableHeader}>
                      <Text style={[styles.tableHeaderCell, styles.colDate]}>시작 시간</Text>
                      <Text style={[styles.tableHeaderCell, styles.colDate]}>완료 시간</Text>
                      <Text style={[styles.tableHeaderCell, styles.colStatus]}>상태</Text>
                      <Text style={[styles.tableHeaderCell, styles.colRecords]}>처리 건수</Text>
                      <Text style={[styles.tableHeaderCell, styles.colDuration]}>소요 시간</Text>
                    </View>
                    {executionHistory.map((item) => (
                      <View key={item.id} style={styles.tableRow}>
                        <Text style={[styles.tableCell, styles.colDate]}>
                          {formatDate(item.startedAt)}
                        </Text>
                        <Text style={[styles.tableCell, styles.colDate]}>
                          {formatDate(item.completedAt, true, item.status)}
                        </Text>
                        <View style={[styles.tableCell as any, styles.colStatus]}>
                          <View style={[
                            styles.statusBadge,
                            { backgroundColor: getStatusColor(item.status) }
                          ]}>
                            <Text style={styles.statusBadgeText}>
                              {getStatusText(item.status)}
                            </Text>
                          </View>
                        </View>
                        <Text style={[styles.tableCell, styles.colRecords]}>
                          {item.recordsProcessed}
                        </Text>
                        <Text style={[styles.tableCell, styles.colDuration]}>
                          {formatDuration(item.startedAt, item.completedAt, item.status)}
                        </Text>
                      </View>
                    ))}
                  </View>
                  
                  {totalPages > 1 && (
                    <View style={styles.pagination}>
                      <TouchableOpacity
                        style={[styles.pageButton, currentPage === 1 && styles.pageButtonDisabled]}
                        onPress={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                      >
                        <Text style={[styles.pageButtonText, currentPage === 1 && { color: '#ccc' }]}>
                          이전
                        </Text>
                      </TouchableOpacity>
                      
                      <Text style={styles.pageInfo}>
                        {currentPage} / {totalPages}
                      </Text>
                      
                      <TouchableOpacity
                        style={[styles.pageButton, currentPage === totalPages && styles.pageButtonDisabled]}
                        onPress={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                      >
                        <Text style={[styles.pageButtonText, currentPage === totalPages && { color: '#ccc' }]}>
                          다음
                        </Text>
                      </TouchableOpacity>
                    </View>
                  )}
                </>
              ) : (
                <View style={styles.emptyState}>
                  <Text style={styles.emptyStateText}>실행 이력이 없습니다</Text>
                </View>
              )}
            </View>
          </View>
        )}
      </ScrollView>
    </View>
  );
  
  return renderContent();
};

export default CronJobDashboard;